import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { ROLE, ROLE_OPTIONS } from '../../constants';
import getRoleOption from '../../utils/getRoleOption';
import type { Role, SelectOption } from '../../types/common';
import type { OrganizationType } from '../../types/organization';

interface SelectRoleProps {
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  selected: Role[];
  selectedOrganization: OrganizationType[];
  onChange?: (role: Role[]) => void;
}

/**
 * ロールを選択するコンポーネント。Role[]を引数に取るコールバックがあれば実行する。
 * @param id? [string]
 * @param className? [string]
 * @param disabled? [boolean]
 * @param isClearable? [boolean]
 * @param selected [OptionsType | null]
 * @param selectedOrganization? [OrganizationType[]]
 * @param onChange?: (role: Role[]) => void;
 * @returns React.ReactElement
 */
const SelectRole = ({
  id = 'selectRole',
  className = '',
  disabled = false,
  isClearable = false,
  selected = [],
  selectedOrganization = [],
  onChange = () => {},
}: SelectRoleProps): React.ReactElement => {
  const { t } = useTranslation();
  const selectedRole: SelectOption<Role[]> = getRoleOption(selected);
  const [selectedOption, setSelectedOption] = useState<SelectOption<Role[]> | null>(selectedRole);

  // ロールのプルダウンオプションのコントロール
  // ・（複数）選択されている組織の中に商社組織（今は豊田通商のみ）が選ばれていたらアドミンとパワーユーザだけにする
  // ・（複数）選択されている組織の中に商社組織（今は豊田通商のみ）が選ばれていなかったらユーザだけにする

  const roleOptions: SelectOption<Role[]>[] = Object.values(ROLE_OPTIONS);
  const userOnly = roleOptions.filter((option) => option.value[0] === ROLE.USER);
  const adminAndPowerusers = roleOptions.filter((option) => option.value[0] === ROLE.ADMIN || option.value[0] === ROLE.POWERUSER);
  const [options, setOptions] = useState<SelectOption<Role[]>[]>(userOnly);

  // SIDE EFFECT
  // ================================================================================

  useEffect(() => {
    const isTradingCompanySelected: boolean = selectedOrganization.filter((org) => org.isTradingCompany).length > 0;
    setOptions(isTradingCompanySelected ? adminAndPowerusers : userOnly);
  }, [selectedOrganization]);

  // 確認用
  // useEffect(() => {
  // }, []);

  // EVENT HANDLER
  // ================================================================================

  // プルダウンから組織を選んだとき
  const handleSelectChange = (option: SelectOption<Role[]>) => {
    setSelectedOption(option);
    onChange(option.value);
  };

  // VIEW
  // ================================================================================

  return (
    <Select
      inputId={id}
      options={options}
      value={selectedOption}
      onChange={handleSelectChange}
      isDisabled={disabled}
      isClearable={isClearable}
      placeholder={t('dropdown.role')}
      className={'react-select-container' + ' ' + className}
      classNamePrefix="react-select"
    />
  );
};

export default SelectRole;
